import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Loading from '../Components/Loading';
import TimePicker from '../Components/TimePicker';
import { API_URL } from '../config/api';

const LeaveApply = () => {
  const [formData, setFormData] = useState({
    leaveType: '',
    startDate: '',
    endDate: '',
    numberOfDays: '',
    reason: '',
    hours: '', // For permission type only
    ondutyFromDate: '', // For onduty type
    ondutyFromTime: '', // For onduty type
    ondutyToDate: '', // For onduty type
    ondutyToTime: '' // For onduty type
  });
  const [errors, setErrors] = useState({});
  const [ondutyHours, setOndutyHours] = useState(null);
  const [loading, setLoading] = useState(false);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [loadingBalance, setLoadingBalance] = useState(false);
  const [isHalfDay, setIsHalfDay] = useState(false);
  const [halfDayPosition, setHalfDayPosition] = useState('first-day'); // 'first-day' or 'last-day' (for multiple days)
  const [halfDayFromTime, setHalfDayFromTime] = useState(''); // Start time for half-day (HH:MM format)
  const [halfDayToTime, setHalfDayToTime] = useState(''); // End time for half-day (HH:MM format)

  useEffect(() => {
    fetchLeaveBalance();
  }, []);

  useEffect(() => {
    // For permission type, endDate must equal startDate (single day)
    if (formData.leaveType === 'permission' && formData.startDate) {
      setFormData(prev => ({ ...prev, endDate: formData.startDate }));
    }
    // Note: Half-day now allows date ranges, so we don't auto-set endDate
  }, [formData.leaveType, formData.startDate]);

  useEffect(() => {
    if (formData.leaveType === 'Travel') {
      // For Travel, calculate based on date-time range
      if (formData.ondutyFromDate && formData.ondutyFromTime && formData.ondutyToDate && formData.ondutyToTime) {
        calculateDays();
      } else {
        setFormData(prev => ({ ...prev, numberOfDays: '' }));
      }
    } else if (formData.startDate && formData.endDate) {
      calculateDays();
    } else {
      setFormData(prev => ({ ...prev, numberOfDays: '' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.startDate, formData.endDate, formData.leaveType, formData.hours, formData.ondutyFromDate, formData.ondutyFromTime, formData.ondutyToDate, formData.ondutyToTime, isHalfDay]);

  const fetchLeaveBalance = async () => {
    const employeeId = localStorage.getItem('id');
    
    if (!employeeId) {
      return;
    }

    if (!API_URL) {
      return;
    }

    setLoadingBalance(true);
    try {
      const payload = {
        action: 'get-leave-balance',
        employeeId: employeeId,
        year: new Date().getFullYear()
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;

      if (result?.success) {
        setLeaveBalance(result.balance);
      }
    } catch (error) {
      console.error('Error fetching leave balance:', error);
    } finally {
      setLoadingBalance(false);
    }
  };

  // Format date from YYYY-MM-DD to DD-MMM format for display
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString + 'T00:00:00');
      if (isNaN(date.getTime())) return dateString;
      const day = date.getDate().toString().padStart(2, '0');
      const month = date.toLocaleString('default', { month: 'short' });
      return `${day}-${month}`;
    } catch (error) {
      return dateString;
    }
  };

  const calculateDays = () => {
    // For Travel, check Travel date-time fields
    if (formData.leaveType === 'Travel') {
      if (!formData.ondutyFromDate || !formData.ondutyFromTime || !formData.ondutyToDate || !formData.ondutyToTime) {
        setFormData(prev => ({ ...prev, numberOfDays: '' }));
        return;
      }
    } else if (!formData.startDate || !formData.endDate) {
      setFormData(prev => ({ ...prev, numberOfDays: '' }));
      setOndutyHours(null);
      return;
    }

    try {
      // For permission type, calculate based on selected hours
      if (formData.leaveType === 'permission') {
        if (!formData.hours) {
          setFormData(prev => ({ ...prev, numberOfDays: '' }));
          return;
        }
        const hours = parseFloat(formData.hours);
        const days = hours / 8; // Convert hours to days (8 hours = 1 day)
        setFormData(prev => ({ ...prev, numberOfDays: days }));
        setOndutyHours(null);
        return;
      }

      // For Travel type, calculate based on date-time range
      if (formData.leaveType === 'Travel') {
        if (!formData.ondutyFromDate || !formData.ondutyFromTime || !formData.ondutyToDate || !formData.ondutyToTime) {
          setFormData(prev => ({ ...prev, numberOfDays: '' }));
          return;
        }

        // Parse date and time
        const fromDateTime = new Date(`${formData.ondutyFromDate}T${formData.ondutyFromTime}`);
        const toDateTime = new Date(`${formData.ondutyToDate}T${formData.ondutyToTime}`);

        // Validate dates
        if (isNaN(fromDateTime.getTime()) || isNaN(toDateTime.getTime())) {
          setFormData(prev => ({ ...prev, numberOfDays: '' }));
          return;
        }

        // Check if "to" is after "from"
        if (toDateTime <= fromDateTime) {
          setErrors(prev => ({ ...prev, ondutyToDate: 'To date/time must be after From date/time' }));
          setFormData(prev => ({ ...prev, numberOfDays: '' }));
          return;
        }

        // Clear error if valid
        setErrors(prev => {
          const newErrors = { ...prev };
          if (newErrors.ondutyToDate) {
            delete newErrors.ondutyToDate;
          }
          return newErrors;
        });

        // Calculate total hours between from and to date/time
        const totalMilliseconds = toDateTime.getTime() - fromDateTime.getTime();
        const totalHours = totalMilliseconds / (1000 * 60 * 60);
        
        // For Travel, we calculate actual hours worked, not calendar days
        // But we cap it at 8 hours per calendar day
        const startDate = new Date(fromDateTime.getFullYear(), fromDateTime.getMonth(), fromDateTime.getDate());
        const endDate = new Date(toDateTime.getFullYear(), toDateTime.getMonth(), toDateTime.getDate());
        const calendarDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
        
        // Cap total hours at 8 hours per calendar day
        const maxHours = calendarDays * 8;
        const actualHours = Math.min(totalHours, maxHours);
        
        const roundedHours = Math.round(actualHours * 100) / 100;
        const days = roundedHours / 8;
        const roundedDays = Math.round(days * 100) / 100;
        
        setOndutyHours(roundedHours);
        setFormData(prev => ({ ...prev, numberOfDays: roundedDays }));
        return;
      }

      // Parse dates - date input gives YYYY-MM-DD format
      // Create dates in local timezone to avoid timezone issues
      const startParts = formData.startDate.split('-');
      const endParts = formData.endDate.split('-');
      
      if (startParts.length !== 3 || endParts.length !== 3) {
        setFormData(prev => ({ ...prev, numberOfDays: '' }));
        return;
      }
      setOndutyHours(null);
      
      const start = new Date(parseInt(startParts[0]), parseInt(startParts[1]) - 1, parseInt(startParts[2]));
      const end = new Date(parseInt(endParts[0]), parseInt(endParts[1]) - 1, parseInt(endParts[2]));

      // Validate dates
      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        setFormData(prev => ({ ...prev, numberOfDays: '' }));
        return;
      }

      // Check if end date is before start date
      if (end < start) {
        setErrors(prev => ({ ...prev, endDate: 'End date must be after or equal to start date' }));
        setFormData(prev => ({ ...prev, numberOfDays: '' }));
        return;
      }

      // Clear end date error if dates are valid
      setErrors(prev => {
        const newErrors = { ...prev };
        if (newErrors.endDate) {
          delete newErrors.endDate;
        }
        return newErrors;
      });

      // Calculate days including all days (no weekend exclusion for leave applications)
     let days = 0;
let currentDate = new Date(start);

while (currentDate <= end) {
  const day = currentDate.getDay();

  if (day !== 0) { 
    days++;
  }

  currentDate.setDate(currentDate.getDate() + 1);
}

      // For half-day leave, subtract 0.5 from total days
      if (isHalfDay && ['casual', 'sick', 'earned', 'lop'].includes(formData.leaveType)) {
        const halfDayCount = days - 0.5;
        setFormData(prev => ({ ...prev, numberOfDays: halfDayCount }));
      } else {
        setFormData(prev => ({ ...prev, numberOfDays: days }));
      }
    } catch (error) {
      console.error('Error calculating days:', error);
      setFormData(prev => ({ ...prev, numberOfDays: '' }));
    }
  };

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    
    // Handle checkbox for half-day
    if (name === 'isHalfDay') {
      setIsHalfDay(checked);
      // If unchecking half-day, reset half-day times and position
      if (!checked) {
        setHalfDayFromTime('');
        setHalfDayToTime('');
        setHalfDayPosition('first-day');
      }
      return;
    }

    // Handle half-day position selection (which day in the range - only for multiple days)
    if (name === 'halfDayPosition') {
      setHalfDayPosition(value);
      return;
    }

    // Handle half-day time inputs
    if (name === 'halfDayFromTime') {
      setHalfDayFromTime(value);
      // Clear error if exists
      if (errors.halfDayFromTime) {
        setErrors(prev => ({ ...prev, halfDayFromTime: '' }));
      }
      return;
    }

    if (name === 'halfDayToTime') {
      setHalfDayToTime(value);
      // Clear error if exists
      if (errors.halfDayToTime) {
        setErrors(prev => ({ ...prev, halfDayToTime: '' }));
      }
      // Validate that to time is after from time
      if (halfDayFromTime && value && halfDayFromTime >= value) {
        setErrors(prev => ({ ...prev, halfDayToTime: 'End time must be after start time' }));
      }
      return;
    }
    
    // If changing leave type, reset hours, endDate, and half-day option
    if (name === 'leaveType') {
      setIsHalfDay(false); // Reset half-day when changing leave type
      setHalfDayPosition('first-day'); // Reset half-day position
      setHalfDayFromTime(''); // Reset half-day times
      setHalfDayToTime('');
      setFormData(prev => ({
        ...prev,
        [name]: value,
        hours: '', // Reset hours - user must select from dropdown
        endDate: value === 'permission' && prev.startDate ? prev.startDate : prev.endDate,
        // Reset onduty fields when changing leave type
        ondutyFromDate: '',
        ondutyFromTime: '',
        ondutyToDate: '',
        ondutyToTime: ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    // Clear calculated hours when inputs change
    if (formData.leaveType === 'Travel' && ['ondutyFromDate', 'ondutyFromTime', 'ondutyToDate', 'ondutyToTime'].includes(name)) {
      setOndutyHours(null);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (formData.numberOfDays === 0) {
  newErrors.numberOfDays = 'Leave cannot be applied only on Sunday';
}

    const isSunday = (dateString) => {
  const date = new Date(dateString + 'T00:00:00');
  return date.getDay() === 0; 
};


if (formData.startDate && isSunday(formData.startDate)) {
  newErrors.startDate = 'Sunday cannot be selected for leave';
}

if (formData.endDate && isSunday(formData.endDate)) {
  newErrors.endDate = 'Sunday cannot be selected for leave';
}


    if (!formData.leaveType) {
      newErrors.leaveType = 'Leave type is required';
    }

    // Validate start/end date for non-Travel types
    if (formData.leaveType !== 'Travel') {
      if (!formData.startDate) {
        newErrors.startDate = 'Start date is required';
      }

      if (!formData.endDate) {
        newErrors.endDate = 'End date is required';
      } else if (formData.startDate) {
        const start = new Date(formData.startDate + 'T00:00:00');
        const end = new Date(formData.endDate + 'T00:00:00');
        
        // For permission, start and end date must be the same
        if (formData.leaveType === 'permission') {
          if (start.toDateString() !== end.toDateString()) {
            newErrors.endDate = 'Permission must be for a single day';
          }
        } 
        // For half-day leave, allow date ranges (e.g., 2.5 days)
        else if (end < start) {
          newErrors.endDate = 'End date must be after or equal to start date';
        }
      }
    }

    // Validate hours for permission type
    if (formData.leaveType === 'permission') {
      if (!formData.hours) {
        newErrors.hours = 'Hours is required for permission';
      } else {
        const hours = parseFloat(formData.hours);
        if (isNaN(hours) || hours <= 0) {
          newErrors.hours = 'Hours must be greater than 0';
        }
      }
    }

    // Validate date-time range for Travel type
    if (formData.leaveType === 'Travel') {
      if (!formData.ondutyFromDate) {
        newErrors.ondutyFromDate = 'From date is required for Travel';
      }
      if (!formData.ondutyFromTime) {
        newErrors.ondutyFromTime = 'From time is required for Travel';
      }
      if (!formData.ondutyToDate) {
        newErrors.ondutyToDate = 'To date is required for Travel';
      }
      if (!formData.ondutyToTime) {
        newErrors.ondutyToTime = 'To time is required for Travel';
      }

      // Validate that "to" is after "from"
      if (formData.ondutyFromDate && formData.ondutyFromTime && formData.ondutyToDate && formData.ondutyToTime) {
        const fromDateTime = new Date(`${formData.ondutyFromDate}T${formData.ondutyFromTime}`);
        const toDateTime = new Date(`${formData.ondutyToDate}T${formData.ondutyToTime}`);
        
        if (!isNaN(fromDateTime.getTime()) && !isNaN(toDateTime.getTime())) {
          if (toDateTime <= fromDateTime) {
            newErrors.ondutyToDate = 'To date/time must be after From date/time';
            newErrors.ondutyToTime = 'To date/time must be after From date/time';
          }
        }
      }
    }

    // Validate half-day time inputs
    if (isHalfDay && ['casual', 'sick', 'earned', 'lop'].includes(formData.leaveType)) {
      if (!halfDayFromTime) {
        newErrors.halfDayFromTime = 'Start time is required for half-day leave';
      }
      if (!halfDayToTime) {
        newErrors.halfDayToTime = 'End time is required for half-day leave';
      }
      
      // Validate that end time is after start time
      if (halfDayFromTime && halfDayToTime) {
        // Convert to minutes for comparison (HH:MM format)
        const [fromHour, fromMin] = halfDayFromTime.split(':').map(Number);
        const [toHour, toMin] = halfDayToTime.split(':').map(Number);
        const fromMinutes = fromHour * 60 + fromMin;
        const toMinutes = toHour * 60 + toMin;
        
        if (toMinutes <= fromMinutes) {
          newErrors.halfDayToTime = 'End time must be after start time';
        }
      }
    }

    if (!formData.numberOfDays || formData.numberOfDays <= 0) {
      newErrors.numberOfDays = 'Number of days must be greater than 0';
    }

    // Validate reason field (mandatory)
    if (!formData.reason || !formData.reason.trim()) {
      newErrors.reason = 'Reason is required';
    }

    // Check balance for leaves that require balance (exclude LOP, permission, Travel)
    if (formData.leaveType && !['lop', 'permission', 'Travel'].includes(formData.leaveType) && leaveBalance) {
      const type = formData.leaveType;
      const balance = leaveBalance[type]?.balance || 0;
      
      if (formData.numberOfDays > balance) {
        newErrors.numberOfDays = `Insufficient balance. Available: ${balance} day(s)`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const employeeId = localStorage.getItem('id');
    
    if (!employeeId) {
      toast.error('User ID not found. Please login again.');
      return;
    }

    setLoading(true);
    try {
      // Recompute Travel hours/days just before submit to guarantee payload
      let finalOndutyHours = ondutyHours;
      let finalNumberOfDays = parseFloat(formData.numberOfDays);

      if (formData.leaveType === 'Travel') {
        const fromDateTime = new Date(`${formData.ondutyFromDate}T${formData.ondutyFromTime}`);
        const toDateTime = new Date(`${formData.ondutyToDate}T${formData.ondutyToTime}`);

        const validRange = !isNaN(fromDateTime.getTime()) && !isNaN(toDateTime.getTime()) && toDateTime > fromDateTime;
        if (validRange) {
          // Calculate total hours between from and to date/time
          const totalMilliseconds = toDateTime.getTime() - fromDateTime.getTime();
          const totalHours = totalMilliseconds / (1000 * 60 * 60);
          
          // Calculate calendar days and cap at 8 hours per day
          const startDate = new Date(fromDateTime.getFullYear(), fromDateTime.getMonth(), fromDateTime.getDate());
          const endDate = new Date(toDateTime.getFullYear(), toDateTime.getMonth(), toDateTime.getDate());
          const calendarDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
          
          // Cap total hours at 8 hours per calendar day
          const maxHours = calendarDays * 8;
          const actualHours = Math.min(totalHours, maxHours);
          
          const roundedHours = Math.round(actualHours * 100) / 100;
          const roundedDays = Math.round((roundedHours / 8) * 100) / 100;
          finalOndutyHours = roundedHours;
          finalNumberOfDays = roundedDays;
        }
      }

      const payload = {
        action: 'apply-leave',
        employeeId: employeeId,
        leaveType: formData.leaveType,
        numberOfDays: finalNumberOfDays,
        // For Travel send date range and times
        ...(formData.leaveType === 'Travel'
          ? {
              startDate: formData.ondutyFromDate,
              endDate: formData.ondutyToDate,
              fromTime: formData.ondutyFromTime,
              toTime: formData.ondutyToTime
            }
          : {
              startDate: formData.startDate,
              endDate: formData.endDate
            }),
        // Include hours for permission type
        ...(formData.leaveType === 'permission' && formData.hours && { hours: parseFloat(formData.hours) }),
        // Include ondutyHours for Travel type (calculated)
        ...(formData.leaveType === 'Travel' && finalOndutyHours !== null && { ondutyHours: finalOndutyHours }),
        // Include half-day details for half-day leaves
        ...(isHalfDay && ['casual', 'sick', 'earned', 'lop'].includes(formData.leaveType) && { 
          halfDayFromTime: halfDayFromTime,
          halfDayToTime: halfDayToTime,
          // For single day, set halfDayPosition to 'first-day' (since there's only one day)
          // For multiple days, use the selected halfDayPosition
          halfDayPosition: formData.startDate === formData.endDate ? 'first-day' : halfDayPosition
        }),
        // Reason is now mandatory
        reason: formData.reason.trim()
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;

      if (result?.success) {
        toast.success(result?.message || 'Leave application submitted successfully');
        // Reset form
        setFormData({
          leaveType: '',
          startDate: '',
          endDate: '',
          numberOfDays: '',
          reason: '',
          hours: '',
          ondutyFromDate: '',
          ondutyFromTime: '',
          ondutyToDate: '',
        ondutyToTime: ''
        });
        setErrors({});
      setOndutyHours(null);
      setIsHalfDay(false);
      setHalfDayPosition('first-day');
      setHalfDayFromTime('');
      setHalfDayToTime('');
        // Refresh balance
        fetchLeaveBalance();
      } else {
        toast.error(result?.message || 'Failed to submit leave application');
      }
    } catch (error) {
      console.error('Error applying leave:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="employee-form">
      <section className="content">
        <div className="container-fluid">
          {/* Header with Title */}
          <div className="row mb-3 employee-header-row">
            <div className="col-md-12">
              <h1 className="employee-title">
                Apply for Leave
                <i className="fas fa-info-circle ml-2 employee-title-icon"></i>
              </h1>
              <p className="employee-description">
                Submit a leave application. Your leave balance will be checked before approval.
              </p>
            </div>
          </div>

          <div className="row">
            {/* Leave Balance Card */}
            <div className="col-md-4">
              <div className="card employee-content-card">
                <div className="card-body">
                  <h5 style={{ marginBottom: '20px', fontSize: '18px', fontWeight: '600' }}>Leave Balance</h5>
                  {loadingBalance ? (
                    <Loading size={80} />
                  ) : leaveBalance ? (
                    <div>
                      <div style={{ marginBottom: '15px', padding: '12px', backgroundColor: '#f3f4f6', borderRadius: '6px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                          <span style={{ fontWeight: '500' }}>Casual:</span>
                          <span style={{ fontWeight: '600', color: '#0094a9' }}>
                            {leaveBalance.casual?.balance || 0} / {leaveBalance.casual?.allocated || 0}
                          </span>
                        </div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>
                          Used: {leaveBalance.casual?.used || 0}
                        </div>
                      </div>
                      <div style={{ marginBottom: '15px', padding: '12px', backgroundColor: '#f3f4f6', borderRadius: '6px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                          <span style={{ fontWeight: '500' }}>Sick:</span>
                          <span style={{ fontWeight: '600', color: '#0094a9' }}>
                            {leaveBalance.sick?.balance || 0} / {leaveBalance.sick?.allocated || 0}
                          </span>
                        </div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>
                          Used: {leaveBalance.sick?.used || 0}
                        </div>
                      </div>
                      <div style={{ marginBottom: '15px', padding: '12px', backgroundColor: '#f3f4f6', borderRadius: '6px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                          <span style={{ fontWeight: '500' }}>Earned:</span>
                          <span style={{ fontWeight: '600', color: '#0094a9' }}>
                            {leaveBalance.earned?.balance || 0} / {((leaveBalance.earned?.carryForward || 0) + (leaveBalance.earned?.allocated || 0))}
                          </span>
                        </div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>
                          Used: {leaveBalance.earned?.used || 0}
                        </div>
                      </div>
                      <div style={{ marginBottom: '15px', padding: '12px', backgroundColor: '#f3f4f6', borderRadius: '6px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                          <span style={{ fontWeight: '500' }}>Permission:</span>
                          <span style={{ fontWeight: '600', color: '#0094a9' }}>
                            {leaveBalance.permission?.balance || 0} / {leaveBalance.permission?.allocated || 0}
                          </span>
                        </div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>
                          Used: {leaveBalance.permission?.used || 0} (Monthly limit: 4 Hr permissions)
                        </div>
                      </div>
                      <div style={{ padding: '12px', backgroundColor: '#f3f4f6', borderRadius: '6px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                          <span style={{ fontWeight: '500' }}>LOP:</span>
                          <span style={{ fontWeight: '600', color: '#ef4444' }}>
                            {leaveBalance.lop?.used || 0}
                          </span>
                        </div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>
                          No limit
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p style={{ color: '#6b7280' }}>Unable to load leave balance</p>
                  )}
                </div>
              </div>
            </div>

            {/* Leave Application Form */}
            <div className="col-md-8">
              <div className="card employee-content-card">
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    <div className="form-group">
                      <label htmlFor="leaveType" className="form-label">
                        Leave Type <span className="text-danger">*</span>
                      </label>
                      <select
                        id="leaveType"
                        name="leaveType"
                        className={`form-control ${errors.leaveType ? 'is-invalid' : ''}`}
                        value={formData.leaveType}
                        onChange={handleChange}
                        required
                        style={{ padding: '6px 10px', fontSize: '14px', height: '36px' }}
                      >
                        <option value="">Select Leave Type</option>
                        <option value="casual">Casual</option>
                        <option value="sick">Sick</option>
                        <option value="earned">Earned</option>
                        <option value="lop">LOP (Loss of Pay)</option>
                        <option value="permission">Permission</option>
                        <option value="Travel">Travel</option>
                      </select>
                      {errors.leaveType && (
                        <span className="error-message">{errors.leaveType}</span>
                      )}
                    </div>

                    {/* Regular Start/End Date fields - hidden for Travel */}
                    {formData.leaveType !== 'Travel' && (
                      <div className="row">
                        <div className="col-md-6">
                          <div className="form-group">
                            <label htmlFor="startDate" className="form-label">
                              Start Date <span className="text-danger">*</span>
                            </label>
                            <input
                              type="date"
                              id="startDate"
                              name="startDate"
                              className={`form-control ${errors.startDate ? 'is-invalid' : ''}`}
                              value={formData.startDate}
                              onChange={handleChange}
                              required={formData.leaveType !== 'Travel'}
                              style={{ padding: '6px 10px', fontSize: '14px', height: '36px' }}
                            />
                            {errors.startDate && (
                              <span className="error-message">{errors.startDate}</span>
                            )}
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group">
                            <label htmlFor="endDate" className="form-label">
                              End Date <span className="text-danger">*</span>
                            </label>
                            <input
                              type="date"
                              id="endDate"
                              name="endDate"
                              className={`form-control ${errors.endDate ? 'is-invalid' : ''}`}
                              value={formData.endDate}
                              onChange={handleChange}
                              required={formData.leaveType !== 'Travel'}
                              min={formData.startDate}
                              disabled={formData.leaveType === 'permission'}
                              style={{ 
                                padding: '6px 10px', 
                                fontSize: '14px', 
                                height: '36px',
                                backgroundColor: formData.leaveType === 'permission' ? '#f5f5f5' : 'white',
                                cursor: formData.leaveType === 'permission' ? 'not-allowed' : 'text'
                              }}
                            />
                            {formData.leaveType === 'permission' && (
                              <small style={{ fontSize: '12px', color: '#6b7280' }}>
                                Permission must be for a single day
                              </small>
                            )}
                            {errors.endDate && (
                              <span className="error-message">{errors.endDate}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Half-day checkbox for casual, sick, earned, lop */}
                    {['casual', 'sick', 'earned', 'lop'].includes(formData.leaveType) && (
                      <div className="form-group">
                        <div className="form-check" style={{ marginBottom: isHalfDay ? '15px' : '5px' }}>
                          <input
                            type="checkbox"
                            id="isHalfDay"
                            name="isHalfDay"
                            className="form-check-input"
                            checked={isHalfDay}
                            onChange={handleChange}
                            style={{ marginTop: '6px' }}
                          />
                          <label className="form-check-label" htmlFor="isHalfDay" style={{ fontSize: '14px', cursor: 'pointer' }}>
                            Half Day Leave
                          </label>
                        </div>
                        {isHalfDay && formData.startDate && formData.endDate && (
                          <div style={{ padding: '15px', backgroundColor: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb', marginTop: '10px' }}>
                            {/* Show day selection for multiple days */}
                            {formData.startDate !== formData.endDate && (
                              <div style={{ marginBottom: '20px' }}>
                                <label className="form-label" style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '10px', display: 'block' }}>
                                  Apply Half-Day On <span className="text-danger">*</span>
                                </label>
                                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                                  <div className="form-check" style={{ flex: '1', minWidth: '150px' }}>
                                    <input
                                      type="radio"
                                      id="first-day"
                                      name="halfDayPosition"
                                      className="form-check-input"
                                      value="first-day"
                                      checked={halfDayPosition === 'first-day'}
                                      onChange={handleChange}
                                      style={{ marginTop: '6px' }}
                                    />
                                    <label className="form-check-label" htmlFor="first-day" style={{ fontSize: '14px', cursor: 'pointer', marginLeft: '5px' }}>
                                      First Day {formData.startDate ? `(${formatDateForDisplay(formData.startDate)})` : ''}
                                    </label>
                                  </div>
                                  <div className="form-check" style={{ flex: '1', minWidth: '150px' }}>
                                    <input
                                      type="radio"
                                      id="last-day"
                                      name="halfDayPosition"
                                      className="form-check-input"
                                      value="last-day"
                                      checked={halfDayPosition === 'last-day'}
                                      onChange={handleChange}
                                      style={{ marginTop: '6px' }}
                                    />
                                    <label className="form-check-label" htmlFor="last-day" style={{ fontSize: '14px', cursor: 'pointer', marginLeft: '5px' }}>
                                      Last Day {formData.endDate ? `(${formatDateForDisplay(formData.endDate)})` : ''}
                                    </label>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Time selection for half-day */}
                            <div>
                              <label className="form-label" style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '10px', display: 'block' }}>
                                Half-Day Time Range <span className="text-danger">*</span>
                              </label>
                              <div className="row">
                                <div className="col-md-6">
                                  <div className="form-group">
                                    <label htmlFor="halfDayFromTime" className="form-label" style={{ fontSize: '12px', marginBottom: '5px' }}>
                                      From Time
                                    </label>
                                    <TimePicker
                                      id="halfDayFromTime"
                                      name="halfDayFromTime"
                                      value={halfDayFromTime}
                                      onChange={handleChange}
                                      required
                                      error={errors.halfDayFromTime}
                                      placeholder="09:00"
                                    />
                                    {errors.halfDayFromTime && (
                                      <span className="error-message">{errors.halfDayFromTime}</span>
                                    )}
                                  </div>
                                </div>
                                <div className="col-md-6">
                                  <div className="form-group">
                                    <label htmlFor="halfDayToTime" className="form-label" style={{ fontSize: '12px', marginBottom: '5px' }}>
                                      To Time
                                    </label>
                                    <TimePicker
                                      id="halfDayToTime"
                                      name="halfDayToTime"
                                      value={halfDayToTime}
                                      onChange={handleChange}
                                      required
                                      error={errors.halfDayToTime}
                                      placeholder="13:00"
                                    />
                                    {errors.halfDayToTime && (
                                      <span className="error-message">{errors.halfDayToTime}</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <small style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginTop: '10px' }}>
                                {formData.startDate === formData.endDate
                                  ? 'Select the time range for your half-day leave.'
                                  : `Select the time range for half-day on the selected day. Total days = (date range) - 0.5 days.`}
                              </small>
                            </div>
                          </div>
                        )}
                        {!isHalfDay && (
                          <small style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginTop: '5px' }}>
                            Select this option to apply half-day leave. You can select a date range and choose which day should be half-day.
                          </small>
                        )}
                      </div>
                    )}

                    {/* Hours field for permission type - dropdown with options */}
                    {formData.leaveType === 'permission' && (
                      <div className="form-group">
                        <label htmlFor="hours" className="form-label">
                          Hours <span className="text-danger">*</span>
                        </label>
                        <select
                          id="hours"
                          name="hours"
                          className={`form-control ${errors.hours ? 'is-invalid' : ''}`}
                          value={formData.hours}
                          onChange={handleChange}
                          required
                          style={{ 
                            padding: '6px 10px', 
                            fontSize: '14px', 
                            height: '36px'
                          }}
                        >
                          <option value="">Select Hours</option>
                          <option value="0.5">Half an hour (0.5 hours)</option>
                          <option value="1">One hour (1 hour)</option>
                          <option value="1.5">One and half hour (1.5 hours)</option>
                          <option value="2">Two hours (2 hours)</option>
                        </select>
                        <small style={{ fontSize: '12px', color: '#6b7280' }}>
                          Select the number of hours for permission
                        </small>
                        {errors.hours && (
                          <span className="error-message">{errors.hours}</span>
                        )}
                      </div>
                    )}

                    {/* Date-Time Range for Travel type */}
                    {formData.leaveType === 'Travel' && (
                      <>
                        <div className="row">
                          <div className="col-md-6">
                            <div className="form-group">
                              <label htmlFor="ondutyFromDate" className="form-label">
                                From Date <span className="text-danger">*</span>
                              </label>
                              <input
                                type="date"
                                id="ondutyFromDate"
                                name="ondutyFromDate"
                                className={`form-control ${errors.ondutyFromDate ? 'is-invalid' : ''}`}
                                value={formData.ondutyFromDate}
                                onChange={handleChange}
                                required
                                style={{ padding: '6px 10px', fontSize: '14px', height: '36px' }}
                              />
                              {errors.ondutyFromDate && (
                                <span className="error-message">{errors.ondutyFromDate}</span>
                              )}
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-group">
                              <label htmlFor="ondutyFromTime" className="form-label">
                                From Time <span className="text-danger">*</span>
                              </label>
                              <TimePicker
                                id="ondutyFromTime"
                                name="ondutyFromTime"
                                value={formData.ondutyFromTime}
                                onChange={handleChange}
                                required
                                error={errors.ondutyFromTime}
                                placeholder="--:--"
                              />
                              {errors.ondutyFromTime && (
                                <span className="error-message">{errors.ondutyFromTime}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-md-6">
                            <div className="form-group">
                              <label htmlFor="ondutyToDate" className="form-label">
                                To Date <span className="text-danger">*</span>
                              </label>
                              <input
                                type="date"
                                id="ondutyToDate"
                                name="ondutyToDate"
                                className={`form-control ${errors.ondutyToDate ? 'is-invalid' : ''}`}
                                value={formData.ondutyToDate}
                                onChange={handleChange}
                                required
                                min={formData.ondutyFromDate || ''}
                                style={{ padding: '6px 10px', fontSize: '14px', height: '36px' }}
                              />
                              {errors.ondutyToDate && (
                                <span className="error-message">{errors.ondutyToDate}</span>
                              )}
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-group">
                              <label htmlFor="ondutyToTime" className="form-label">
                                To Time <span className="text-danger">*</span>
                              </label>
                              <TimePicker
                                id="ondutyToTime"
                                name="ondutyToTime"
                                value={formData.ondutyToTime}
                                onChange={handleChange}
                                required
                                error={errors.ondutyToTime}
                                placeholder="--:--"
                              />
                              {errors.ondutyToTime && (
                                <span className="error-message">{errors.ondutyToTime}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <small style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginTop: '10px', marginBottom: '15px' }}>
                          Select the date and time range for on duty. The system will calculate the exact duration.
                        </small>
                      </>
                    )}

                    <div className="form-group">
                      <label htmlFor="numberOfDays" className="form-label">
                        Number of Days
                      </label>
                      <input
                        type="number"
                        id="numberOfDays"
                        name="numberOfDays"
                        className={`form-control ${errors.numberOfDays ? 'is-invalid' : ''}`}
                        value={formData.numberOfDays}
                        readOnly
                        style={{ 
                          padding: '6px 10px', 
                          fontSize: '14px', 
                          height: '36px',
                          backgroundColor: '#f5f5f5',
                          cursor: 'not-allowed'
                        }}
                      />
                      <small style={{ fontSize: '12px', color: '#6b7280' }}>
                        {formData.leaveType === 'permission' 
                          ? ''
                          : isHalfDay 
                            ? 'Half-day leave: Total days calculated as (date range) - 0.5 days'
                            : 'Automatically calculated (Sundays excluded)'}
                      </small>
                      {errors.numberOfDays && (
                        <span className="error-message">{errors.numberOfDays}</span>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="reason" className="form-label">
                        Reason <span className="text-danger">*</span>
                      </label>
                      <textarea
                        id="reason"
                        name="reason"
                        className={`form-control ${errors.reason ? 'is-invalid' : ''}`}
                        value={formData.reason}
                        onChange={handleChange}
                        rows="4"
                        placeholder="Enter reason for leave"
                        required
                        style={{ padding: '6px 10px', fontSize: '14px' }}
                      />
                      {errors.reason && (
                        <span className="error-message">{errors.reason}</span>
                      )}
                    </div>

                    <div className="form-actions" style={{ marginTop: '20px' }}>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={loading}
                        style={{ padding: '8px 20px', fontSize: '14px' }}
                      >
                        {loading ? 'Submitting...' : (
                          formData.leaveType === 'permission' ? 'Submit Permission' :
                          formData.leaveType === 'Travel' ? 'Submit Travel' :
                          'Submit Leave Application'
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LeaveApply;

